import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { createShift, fetchShifts } from "../../redux/actions/shiftActions";
import {
  FiPlus,
  FiCheck,
  FiArrowLeft,
  FiClock,
  FiActivity,
} from "react-icons/fi";
import { useNavigate } from "react-router-dom";

const CreateShift = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { creating, error, success } = useSelector((state) => state.shift);

  const [formData, setFormData] = useState({
    name: "",
    start_time: "",
    end_time: "",
    break_minutes: 60,
    status: "active",
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) return;

    const payload = {
      ...formData,
      break_minutes: Number(formData.break_minutes),
    };

    const ok = await dispatch(createShift(payload));
    if (ok) {
      dispatch(fetchShifts());
    }
  };

  useEffect(() => {
    if (!creating && !error && success) {
      setFormData({
        name: "",
        start_time: "",
        end_time: "",
        break_minutes: 60,
        status: "active",
      });
    }
  }, [creating, error, success]);

  if (creating) {
    return (
      <div className="min-h-screen bg-gray-50 dark:bg-gray-950 flex items-center justify-center">
        <div className="w-12 h-12 border-4 border-cyan-500 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-950 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        <button
          onClick={() => navigate("/dashboard/view-shifts")}
          className="flex items-center gap-2 text-gray-500 mb-6 hover:text-cyan-500 transition-colors"
        >
          <FiArrowLeft /> Back to Shifts
        </button>

        <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl overflow-hidden border border-gray-100 dark:border-gray-800">
          <div className="bg-gradient-to-r from-cyan-600 to-blue-600 p-8 text-white">
            <h1 className="text-2xl font-bold">Create New Shift</h1>
            <p className="text-cyan-100/80">
              Configure work hours and break duration
            </p>
          </div>

          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            <div className="space-y-4">
              <div>
                <label className="text-sm font-bold text-gray-500 dark:text-gray-400 block mb-2">
                  Shift Name
                </label>
                <input
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-cyan-500"
                  placeholder="e.g. General Shift"
                  required
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-gray-500 dark:text-gray-400 block mb-2">
                    Start Time
                  </label>
                  <input
                    type="time"
                    step="1"
                    name="start_time"
                    value={formData.start_time}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500 dark:text-gray-400 block mb-2">
                    End Time
                  </label>
                  <input
                    type="time"
                    step="1"
                    name="end_time"
                    value={formData.end_time}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-bold text-gray-500 dark:text-gray-400 block mb-2">
                    Break (Minutes)
                  </label>
                  <input
                    type="number"
                    name="break_minutes"
                    value={formData.break_minutes}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-cyan-500"
                    required
                  />
                </div>
                <div>
                  <label className="text-sm font-bold text-gray-500 dark:text-gray-400 block mb-2">
                    Status
                  </label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    className="w-full p-4 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-100 dark:border-gray-700 dark:text-white outline-none focus:ring-2 focus:ring-cyan-500"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                  </select>
                </div>
              </div>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="button"
                onClick={() =>
                  setFormData({
                    name: "",
                    start_time: "",
                    end_time: "",
                    break_minutes: 60,
                    status: "active",
                  })
                }
                className="flex-1 py-4 rounded-xl bg-gray-100 dark:bg-gray-800 text-gray-500 font-bold hover:bg-gray-200 transition-all"
              >
                Clear
              </button>
              <button
                type="submit"
                className="flex-[2] py-4 rounded-xl bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-bold shadow-lg shadow-cyan-500/30 hover:scale-[1.02] transition-all flex items-center justify-center gap-2"
              >
                <FiPlus /> Create Shift
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateShift;
